// 업로드된 MSDS PDF 파일에서 텍스트를 추출하고, 국내 MSDS 표준 16개 항목 표기 규칙에 맞춰
// Write 화면 입력폼(reviewForm)에 필요한 항목들을 best-effort 로 파싱한다.
// PDF 는 반드시 텍스트 선택이 가능한 문서여야 하며(스캔 이미지 PDF 는 인식 불가),
// 제조사마다 서식이 달라 모든 값이 정확히 추출되지 않을 수 있어 사용자가 검토/수정 후 적용해야 한다.
// legacy 빌드를 사용한다: 기본 build/pdf.js 는 private class field(#) 문법을 써서
// webpack4(vue-cli 3)의 파서가 처리하지 못한다.
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { matchPictogramsFromPdf } from "./pictogramImageMatcher";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL}pdfjs/pdf.worker.min.js`;

// 같은 시각적 줄이라도 표(table) 셀마다 폰트/정렬 차이로 y좌표가 미세하게(수 px) 어긋나는 경우가 많아,
// 정확히 같은 y값이 아니라 이 오차범위 안에 있으면 같은 줄로 묶는다.
const LINE_Y_TOLERANCE = 3;

// PDF 파일(File/Blob) -> PDFDocumentProxy. 텍스트 추출과 그림문자 이미지 매칭이 같은 문서 객체를 공유한다.
// 다 쓴 문서는 반드시 destroyPdfDocument() 로 해제해야 한다. pdf.js 는 워커 한 개를 공유하므로 해제하지 않으면
// 문서마다 워커 메모리가 쌓여, 일괄 재추출처럼 수백~천 건을 연속 처리할 때 브라우저가 멈춘다(실제로 겪은 문제).
async function loadPdfDocument(file) {
  const arrayBuffer = await file.arrayBuffer();
  return pdfjsLib.getDocument({ data: arrayBuffer }).promise;
}

async function destroyPdfDocument(pdf) {
  if (!pdf) return;
  try {
    await pdf.destroy();
  } catch (e) {
    // 해제 실패는 무시한다 - 결과에는 영향이 없다
  }
}

// PDFDocumentProxy -> 텍스트 라인 배열. 대부분의 파싱은 줄 텍스트(text)만 쓰지만, 구성성분 표처럼
// 셀이 여러 줄로 줄바꿈되는 표는 줄 순서만으로 행을 나눌 수 없어 좌표(page, y, 조각별 x)도 함께 남긴다.
async function extractLines(pdf) {
  const lines = [];
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();

    const items = textContent.items
      .filter((it) => it.str && it.str.trim())
      .map((it) => ({ str: it.str, x: it.transform[4], y: it.transform[5], width: it.width || 0 }))
      .sort((a, b) => b.y - a.y || a.x - b.x);

    let currentRow = [];
    let currentY = null;
    const flushRow = () => {
      if (currentRow.length === 0) return;

      // 브라우저가 만든 PDF는 한글 글자 하나하나가 별도 텍스트 조각으로 분리되어 있는 경우가 많다.
      // 조각 사이를 무조건 띄어쓰기로 이으면 "제품명"이 "제 품 명"이 되어 모든 라벨 매칭이 깨지므로,
      // 실제 시각적 간격(gap)이 글자 폭 대비 충분히 클 때만 공백을 넣는다.
      const sorted = currentRow.slice().sort((a, b) => a.x - b.x);
      let text = "";
      let prevEndX = null;
      for (const it of sorted) {
        if (prevEndX !== null) {
          const gap = it.x - prevEndX;
          const threshold = Math.max(1, it.width * 0.2);
          if (gap > threshold) text += " ";
        }
        text += it.str;
        prevEndX = it.x + it.width;
      }

      text = text.replace(/\s+/g, " ").trim();
      if (text) lines.push({ text, page: pageNum, y: currentY, items: sorted });
      currentRow = [];
    };

    for (const item of items) {
      if (currentY !== null && Math.abs(item.y - currentY) <= LINE_Y_TOLERANCE) {
        currentRow.push(item);
        // 드리프트(오차가 누적되어 다른 줄까지 묶이는 것) 방지를 위해 평균 y로 기준을 갱신한다
        currentY = currentRow.reduce((sum, it2) => sum + it2.y, 0) / currentRow.length;
      } else {
        flushRow();
        currentRow = [item];
        currentY = item.y;
      }
    }
    flushRow();
  }
  return lines;
}

// 표에서 <th rowspan="N">제목</th> 처럼 절 제목이 여러 데이터 행에 걸쳐 세로로 가운데 정렬된 경우,
// 브라우저가 만든 PDF에서는 그 제목 텍스트의 y좌표가 데이터 행들의 "중간"에 위치해 실제로는
// 제목이 자기 내용의 앞이 아니라 중간에 끼어 나온다. 그래서 "제목 다음 줄부터" 식으로는 앞쪽 내용을
// 놓치므로, 제목 줄을 기준으로 다른 절의 경계를 만나기 전까지 앞뒤로 모두 확장해서 모은다.
const UNIVERSAL_SECTION_BOUNDARIES = [
  /^유해\s*[·ㆍ]?\s*위험\s*문구/,
  /^예방\s*조치\s*문구/,
  /^공급자\s*정보/,
  /^구성성분/,
  /^취급\s*부서/,
  /^그\s*림\s*문\s*자/,
  /^신\s*호\s*어/,
  /^제품\s*명/,
  /^제조사\s*\(?\s*공급사?\s*\)?/,
  /^전화번호/,
  // 절 번호 표기는 "3. 구성성분", "3) 구성성분" 외에 "3:구성성분" 형태도 있다
  /^\d+\s*[.):]\s*[가-힣]/,
  /^(위험|경고|없음)\s*(?:\((?:Danger|Warning)\))?$/i,
];

// 다음 줄이 실제 값이 아니라 다른 절의 제목/그림문자 라벨 나열일 가능성이 높은 경우를 걸러낸다
function looksLikeNonValueLine(line) {
  if (UNIVERSAL_SECTION_BOUNDARIES.some((r) => r.test(line))) return true;

  const pictogramWords = ["폭발성", "인화성", "산화성", "고압가스", "부식성", "급성독성", "경고", "건강유해성", "수생환경유해성", "수생환경 유해성"];
  const tokens = line.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2 && tokens.every((t) => pictogramWords.includes(t))) return true;

  return false;
}

function findValueAfterLabel(lines, labelRegex, { sameLine = true, nextLine = true } = {}) {
  for (let i = 0; i < lines.length; i++) {
    const match = lines[i].match(labelRegex);
    if (!match) continue;

    if (sameLine) {
      const rest = lines[i].slice(match.index + match[0].length).replace(/^[:：\s]+/, "").trim();
      if (rest) return rest;
    }
    if (nextLine && lines[i + 1]) {
      const next = lines[i + 1].trim();
      if (next && !/^\d+[.)]/.test(next) && !looksLikeNonValueLine(next)) return next;
    }
  }
  return "";
}

// 라벨 표기가 문서마다 달라(제조사/회사명/공급자 등) 여러 정규식을 우선순위대로 시도한다
function findValueByLabelPatterns(lines, labelRegexList) {
  for (const labelRegex of labelRegexList) {
    const value = findValueAfterLabel(lines, labelRegex);
    if (value) return value;
  }
  return "";
}

function findFirstMatch(lines, regex) {
  for (const line of lines) {
    const match = line.match(regex);
    if (match) return match;
  }
  return null;
}

// 전화번호 형식(02-000-0000, 010-0000-0000, 041-000-0000 등) 추출
function extractPhone(text) {
  const match = text && text.match(/0\d{1,2}[-.\s]?\d{3,4}[-.\s]?\d{4}/);
  return match ? match[0].replace(/[.\s]/g, "-") : "";
}


// 신호어 값 표기: 한글/영문, 글자 사이 공백, "해당없음·자료없음·분류되지 않음" 류의 없음 표기를 모두 받는다
const SIGNAL_VALUE = /(위\s*험|경\s*고|danger|warning|해당\s*없음|자료\s*없음|분류되지\s*않음|없\s*음|not\s+applicable|none)/i;
const SIGNAL_LABEL = /(?:신\s*호\s*어|signal\s*words?)/i;

function normalizeSignalWord(value) {
  const v = value.replace(/\s+/g, "");
  if (/^(위험|danger)$/i.test(v)) return "위험";
  if (/^(경고|warning)$/i.test(v)) return "경고";
  return "없음";
}

// 줄 앞의 글머리표·구분 기호를 떼고 "신호어 신호어 없음" 처럼 라벨이 겹친 것도 정리한다
function stripBullets(line) {
  return line.replace(/[\s:：|·◦○●■□•\-o]+$/g, "").replace(/^[\s:：|·◦○●■□•\-o]+/, "").replace(/[.。]+$/, "").trim();
}

// 신호어 줄에 "위험"과 "경고"가 둘 다 적힌 문서인가(실측: "신호어 : 위험 , 경고"). 어느 쪽인지 사람이 정해야 한다.
function hasAmbiguousSignalWord(lines) {
  for (const line of lines) {
    if (!SIGNAL_LABEL.test(line)) continue;
    const rest = line.replace(new RegExp(`.*${SIGNAL_LABEL.source}`, "i"), "");
    if (/위\s*험|danger/i.test(rest) && /경\s*고|warning/i.test(rest)) return true;
  }
  return false;
}

// 찾지 못하면 "" 를 돌려준다(없음 표기를 실제로 읽은 "없음" 과 구분하기 위해)
function parseSignalWord(lines) {
  for (const line of lines) {
    // 라벨과 값이 같은 줄: "신호어 : 위 험", "Signal word: Danger", "신호어 해당 없음 ."
    const match = line.match(new RegExp(`${SIGNAL_LABEL.source}\\s*[:：]?\\s*[-·]?\\s*${SIGNAL_VALUE.source}`, "i"));
    if (match) return normalizeSignalWord(match[1]);
  }
  // 라벨만 있는 줄 다음에 값이 오는 경우. 값 줄은 신호어 표기로 "시작"해야 한다 —
  // "유해·위험 문구" 같은 다음 항목의 "위험" 을 신호어로 잘못 읽지 않도록(실측: ThreeBond, LITHIPLEX).
  const idx = lines.findIndex((l) => SIGNAL_LABEL.test(l));
  if (idx >= 0) {
    for (let i = idx + 1; i < Math.min(idx + 3, lines.length); i++) {
      const value = stripBullets(lines[i]).replace(/^(신\s*호\s*어\s*)+/, "");
      const match = value.match(new RegExp(`^${SIGNAL_VALUE.source}`, "i"));
      if (match) return normalizeSignalWord(match[1]);
      if (/^(유해|예방|그림|pictogram|hazard)/i.test(value)) break;
    }
  }
  // 라벨 없이 "위험(Danger)" / "경고(Warning)" / "없음" 배너 형태로만 표기된 경우
  for (const line of lines) {
    const match = line.trim().match(/^(위험|경고|없음)\s*(?:\((?:Danger|Warning)\))?$/i);
    if (match) return match[1];
  }
  return "";
}

const GHS_PICTOGRAM_KEYWORDS = {
  GHS01: ["폭발성"],
  GHS02: ["인화성"],
  GHS03: ["산화성"],
  GHS04: ["고압가스"],
  GHS05: ["부식성"],
  GHS06: ["급성독성"],
  GHS07: ["경고"],
  GHS08: ["건강유해성"],
  GHS09: ["수생환경유해성", "수생환경 유해성"],
};

function parsePictograms(lines) {
  const fullText = lines.join(" ");
  const result = {};
  for (const key of Object.keys(GHS_PICTOGRAM_KEYWORDS)) {
    result[key] = false;
  }

  const ghsCodeMatches = fullText.match(/GHS0[1-9]/g) || [];
  for (const code of ghsCodeMatches) {
    result[code] = true;
  }

  // 그림문자 라벨 인근 구간에서 위험 유형 키워드로도 보조 판단한다 ("경고" 는 신호어와 겹쳐 오탐이 잦으므로 GHS 코드가 없을 때만 사용)
  // "그 림문자" 처럼 글자 사이가 벌어진 PDF(실측: YG6111)도 라벨로 인식한다
  const sectionIdx = lines.findIndex((l) => /그\s*림\s*문\s*자/.test(l));
  if (sectionIdx >= 0) {
    const sectionText = lines.slice(sectionIdx, sectionIdx + 15).join(" ");
    for (const [key, keywords] of Object.entries(GHS_PICTOGRAM_KEYWORDS)) {
      if (result[key]) continue;
      if (key === "GHS07") continue;
      if (keywords.some((kw) => sectionText.includes(kw))) result[key] = true;
    }
  }

  // "그림문자" 섹션 표기나 GHS 코드 없이, 이미지 대체텍스트만 남아 픽토그램 라벨 단어들이
  // 한 줄에 나열되는 경우("폭발성 인화성 산화성 고압가스 부식성")를 보조로 인식한다.
  // 줄의 모든 단어가 픽토그램 라벨과 정확히 일치할 때만 인정해 일반 문구와의 오탐을 막는다.
  // 주의: 실제 제조사 MSDS PDF 는 그림문자를 이미지(벡터/래스터)로만 넣고 텍스트 캡션이 없는
  // 경우가 대부분이라, 이 방식으로는 인식되지 않을 수 있다 - 그런 경우는 검토 화면에서 직접 체크한다.
  const labelToKey = {};
  for (const [key, keywords] of Object.entries(GHS_PICTOGRAM_KEYWORDS)) {
    for (const kw of keywords) labelToKey[kw] = key;
  }
  for (const line of lines) {
    const tokens = line.trim().split(/\s+/).filter(Boolean);
    if (tokens.length < 2) continue;
    if (tokens.every((t) => labelToKey[t])) {
      for (const t of tokens) result[labelToKey[t]] = true;
    }
  }

  return result;
}

// 브라우저 인쇄(Ctrl+P)로 만든 PDF에 흔히 붙는 날짜/URL/페이지번호 등 머리글·꼬리글 라인을 제거한다
function stripPrintNoise(lines) {
  const noiseRegexes = [
    /^\d{1,4}\.\s*\d{1,2}\.\s*\d{1,2}\.\s*(오전|오후)\s*\d{1,2}:\d{2}/,
    /^(https?:\/\/|localhost)/i,
    /^\d+\s*\/\s*\d+$/,
    // 제조사 MSDS 머리글의 페이지 번호 ("페이지: 3(16)", "페이지 3/16")
    /^페이지\s*[:：]?\s*\d+\s*(?:\(\s*\d+\s*\)|\/\s*\d+)?$/,
  ];
  return lines.filter((line) => !noiseRegexes.some((r) => r.test(line.text.trim())));
}

// 인접한 두 절(예: 유해위험문구 -> 예방조치문구)이 둘 다 rowspan 제목을 갖고 있으면, 앞 절의 마지막
// 데이터 행과 뒤 절의 첫 데이터 행이 둘 다 "제목이 아직 등장하지 않은" 상태로 붙어 나올 수 있다.
// 그래서 단순히 "다음 경계 전까지"로는 뒤 절의 시작 부분을 앞 절이 먹어버리는 문제가 생긴다.
// 모든 절 제목의 인덱스를 먼저 구해두고, 현재 제목과 그 앞뒤에서 가장 가까운 다른 제목의 "중간 지점"으로
// 경계를 나누면, 제목이 자기 데이터 행들 중간 어디에 있든 앞뒤 절 사이를 절반씩 정확히 나눌 수 있다.
function collectSectionLinesAround(lines, headerRegex) {
  const headerIdx = lines.findIndex((l) => headerRegex.test(l.trim()));
  if (headerIdx < 0) return [];

  const boundaryIndexes = [];
  lines.forEach((l, i) => {
    if (UNIVERSAL_SECTION_BOUNDARIES.some((r) => r.test(l.trim()))) boundaryIndexes.push(i);
  });

  const prevBoundaryIdx = Math.max(-1, ...boundaryIndexes.filter((i) => i < headerIdx));
  const nextBoundaryIdx = Math.min(lines.length, ...boundaryIndexes.filter((i) => i > headerIdx));

  const start = Math.ceil((prevBoundaryIdx + headerIdx) / 2);
  const end = Math.floor((headerIdx + nextBoundaryIdx) / 2);

  // 헤더 셀이 같은 y좌표로 묶여 첫 데이터 행과 한 줄로 합쳐지는 경우("유해·위험문구 고인화성 액체 및
  // 증기")가 있어, 단순히 헤더 정규식과 일치하는 줄 전체를 버리면 그 안에 딸려 있던 내용까지 함께
  // 사라진다. 헤더(또는 다른 절 경계) 접두어만 잘라내고 남는 텍스트가 있으면 내용으로 살린다.
  const collected = [];
  for (let i = start; i <= end; i++) {
    const rawLine = lines[i].trim();
    if (!rawLine) continue;

    let matched = null;
    for (const r of UNIVERSAL_SECTION_BOUNDARIES) {
      const m = rawLine.match(r);
      if (m) {
        matched = m;
        break;
      }
    }

    if (matched) {
      const rest = rawLine.slice(matched.index + matched[0].length).trim();
      if (rest) collected.push(rest);
      continue;
    }
    collected.push(rawLine);
  }
  return collected;
}

// H/P 코드 문구는 "2. 유해성·위험성" 절에만 있어야 한다. 문서 전체를 훑으면 11.독성/15.법적규제 절의
// 성분별 분류표("톨루엔 Flam. Liq. 2 (H225) ...")까지 경고문구로 들어가므로 2절 범위로 한정한다.
// 절 안의 하위 항목도 "2)신호어", "3)유해·위험문구" 처럼 번호가 붙으므로, 시작은 "유해", 끝은 "구성"
// 이 들어간 줄로 판별한다. 절 번호가 없는 서식(이 앱의 인쇄본 등)은 기존처럼 문서 전체를 쓴다.
function getHazardSectionLines(lines) {
  const startIdx = lines.findIndex((l) => /^2\s*[.):]/.test(l) && l.includes("유해"));
  if (startIdx < 0) return lines;
  const endIdx = lines.findIndex((l, i) => i > startIdx && /^3\s*[.):]/.test(l) && l.includes("구성"));
  return lines.slice(startIdx, endIdx > startIdx ? endIdx : lines.length);
}

// "H225 : 고인화성 액체 및 증기" 처럼 줄 전체가 코드+문구인 경우는 물론, "∘ 유해·위험문구 H317 …"
// 처럼 왼쪽 라벨 셀과 같은 줄로 묶여 코드가 줄 중간부터 시작하는 경우까지 잡기 위해 줄 시작(^)에
// 앵커하지 않는다. H/P 코드 자체가 충분히 특이한 패턴이라 오탐 위험은 낮다.
// 단, "Flam. Liq. 2 (H225)" 처럼 괄호 안에 든 코드는 11.독성/15.법적규제 절의 분류 표기라 제외한다
// (lookbehind 대신 앞 글자를 소비하는 그룹을 쓴다).
function parseHazardStatements(lines) {
  const result = [];
  const seen = new Set();

  for (const line of lines) {
    const match = line.match(/(?:^|[^(A-Za-z0-9])(H\d{3}[a-zA-Z]?)\s*[:：-]?\s*(.+)$/);
    if (!match) continue;
    const 코드 = match[1];
    const 문구 = match[2].trim();
    if (!문구 || seen.has(코드 + 문구)) continue;
    seen.add(코드 + 문구);
    result.push({ is_checked: false, 코드, 문구 });
  }
  return result;
}

// H코드 표기 없이 "유해ㆍ위험문구" 절 아래에 문구만 줄줄이 나열된 서식(예: 이 앱의 화면 인쇄본)을 위한 대체 파싱
function parseHazardStatementsNoCode(lines) {
  const sectionLines = collectSectionLinesAround(lines, /^유해\s*[·ㆍ]?\s*위험\s*문구/);
  return sectionLines.map((문구) => ({ is_checked: false, 코드: "", 문구 }));
}

// "P210 : 문구" 형태의 예방조치문구 라인 파싱 (구분은 문구 앞 접두어로 best-effort 추정).
// H코드와 마찬가지로 "예방 P261 …"처럼 구분 라벨과 한 줄로 묶이는 경우를 위해 줄 시작에 앵커하지 않는다.
function parsePrecautionStatements(lines) {
  const result = [];
  const seen = new Set();
  let 구분 = "예방";

  for (const line of lines) {
    // 주의: \b(단어 경계)는 한글을 \w 로 취급하지 않아 한글 뒤에서는 항상 실패한다
    // ("대응" 뒤에 공백이 와도 경계로 인식되지 않음) - 공백/줄끝 lookahead 로 대체한다
    if (/^예방(?=\s|$)/.test(line)) 구분 = "예방";
    else if (/^대응(?=\s|$)/.test(line)) 구분 = "대응";
    else if (/^저장(?=\s|$)/.test(line)) 구분 = "저장";
    else if (/^폐기(?=\s|$)/.test(line)) 구분 = "폐기";

    const match = line.match(/(?:^|[^(A-Za-z0-9+])(P\d{3}(?:\+P?\d{3})*)\s*[:：-]?\s*(.+)$/);
    if (!match) continue;
    const 코드 = match[1];
    const 문구 = match[2].trim();
    if (!문구 || seen.has(코드 + 문구)) continue;
    seen.add(코드 + 문구);
    result.push({ is_checked: false, 구분, 코드, 문구 });
  }
  return result;
}

// P코드 표기 없이 "예방조치문구" 절 아래에 문구만 줄줄이 나열된 서식을 위한 대체 파싱 (구분은 알 수 없어 비워둔다)
function parsePrecautionStatementsNoCode(lines) {
  const sectionLines = collectSectionLinesAround(lines, /^예방\s*조치\s*문구/);
  return sectionLines.map((문구) => ({ is_checked: false, 구분: "", 코드: "", 문구 }));
}

// 앞뒤가 숫자/대시로 이어지지 않는 CAS 형태만 후보로 잡는다 - 그래야 "2022-07-13" 같은 날짜에서
// "2022-07-1" 조각을 떼어 CAS 로 오인하지 않는다 (lookbehind 대신 앞 글자를 소비하는 그룹을 쓴다)
const CAS_REGEX = /(?:^|[^\d-])(\d{2,7}-\d{2}-\d)(?![\d-])/g;

// CAS 번호 마지막 자리는 검증숫자다: 앞 숫자들을 오른쪽부터 1,2,3... 을 곱해 더한 값의 일의 자리.
// 형태만 같은 다른 번호(날짜, 문서번호 등)를 걸러내는 데 쓴다.
function isValidCas(cas) {
  const [a, b, check] = cas.split("-");
  const digits = (a + b).split("").reverse();
  const sum = digits.reduce((acc, d, i) => acc + (Number(d) * (i + 1)), 0);
  return sum % 10 === Number(check);
}

// 줄에서 검증숫자가 맞는 첫 CAS 번호와 그 위치를 찾는다
function findCas(line) {
  CAS_REGEX.lastIndex = 0;
  let match = CAS_REGEX.exec(line);
  while (match) {
    const cas = match[1];
    if (isValidCas(cas)) return { CAS: cas, index: match.index + match[0].length - cas.length };
    match = CAS_REGEX.exec(line);
  }
  return null;
}

function hasCas(line) {
  return !!findCas(line);
}

// 실제 제조사 MSDS는 "함유량(%)" 칸에 % 기호 없이 소수 범위만 적는 경우가 많다("60.0~70.0").
// 그렇다고 %? 를 아무 숫자-대시-숫자에나 허용하면 "C1-14-alkyl..." 같은 화학물질명 안의
// 숫자 조각까지 함유량으로 오인한다. %가 없는 범위는 하한에 소수점을 요구해 이 오탐을 줄인다.
// 상한은 "0.1-<1", "0.1~≤1" 처럼 부등호가 붙거나 정수로 적히는 경우도 있어 허용한다.
const PERCENT_REGEX = /(\d{1,3}\.\d+)\s*(?:~|-)\s*[<≤]?\s*(\d{1,3}(?:\.\d+)?)\s*%?|(\d{1,3}(?:\.\d+)?)\s*%/;

function buildIngredientRow(구성물질, CAS, 함량) {
  return {
    is_checked: false,
    구성물질,
    CAS,
    함량,
    성상: "",
    특별관리물질: false,
    관리대상물질: false,
    작업환경측정: false,
    특수건강검진: false,
    인체급성유해성물질: false,
    인체만성유해성물질: false,
    생태유해성물질: false,
    허가물질: false,
    제한물질: false,
    금지물질: false,
    사고대비물질: false,
    배출량조사대상물질: false,
  };
}

function parsePercent(text) {
  const match = text && text.match(PERCENT_REGEX);
  const 함량 = { 범위: "없음", min: "", max: "" };
  if (!match) return { 함량, matchedText: "" };
  if (match[1] && match[2]) {
    함량.범위 = "범위";
    함량.min = match[1];
    함량.max = match[2];
  } else if (match[3]) {
    함량.범위 = "고정값";
    함량.min = match[3];
  }
  return { 함량, matchedText: match[0] };
}

// 함유량 칸이 "범위 | 단일" 두 칸으로 나뉜 서식에서 범위 칸이 "자료없음"(또는 "-")이고 단일 칸에만
// % 기호 없는 정수/소수가 적힌 경우("471-34-1 자료없음 42"). %도 소수점도 없는 숫자는 PERCENT_REGEX 로
// 잡으면 오탐이 많아, CAS 번호 바로 뒤가 "자료없음 숫자" 로만 끝나는 경우로 한정한다.
const SINGLE_COLUMN_PERCENT_REGEX = /^\s*(?:자료\s*없음|-)\s+(\d{1,3}(?:\.\d+)?)\s*%?\s*$/;

function parseSingleColumnPercent(text) {
  const match = text && text.match(SINGLE_COLUMN_PERCENT_REGEX);
  if (!match || Number(match[1]) > 100) return null;
  return { 범위: "고정값", min: match[1], max: "" };
}

// CAS 번호 바로 뒤 칸에 오는 함유량 표기. 이름 칸이 아니라 CAS 뒤로 위치가 한정되므로 PERCENT_REGEX 보다
// 느슨하게 정수 범위("17~20", "1-3")와 부등호("<0.5", ">45"), 잔량 표기("Bal (>45)")까지 허용한다.
// 입력폼은 고정값/범위 두 가지뿐이라 "<0.5" 는 0~0.5, ">45" 는 45~100 범위로 옮긴다.
const AFTER_CAS_NUM = "(\\d{1,3}(?:\\.\\d+)?)";
const AFTER_CAS_RANGE_REGEX = new RegExp(`^${AFTER_CAS_NUM}\\s*%?\\s*[~∼～-]\\s*[<≤]?\\s*${AFTER_CAS_NUM}\\s*%?(?=\\s|$)`);
const AFTER_CAS_LESS_REGEX = new RegExp(`^[<≤]\\s*${AFTER_CAS_NUM}\\s*%?(?=\\s|$)`);
const AFTER_CAS_GREATER_REGEX = new RegExp(`^(?:Bal(?:ance)?\\.?\\s*\\(?\\s*)?[>≥]\\s*${AFTER_CAS_NUM}\\s*%?\\s*\\)?(?=\\s|$)`, "i");

function parsePercentAfterCas(text) {
  // CAS 칸에 식별번호가 함께 적힌 경우("13397-26-7 / KE-04461 40~50") 식별번호를 먼저 떼어낸다
  const t = (text || "").replace(/^\s*\/?\s*(?:KE|EC)\s*[-:]?\s*[\d-]+/i, "").trim();
  const inRange = (...nums) => nums.every((n) => Number(n) <= 100);

  let match = t.match(AFTER_CAS_RANGE_REGEX);
  if (match && inRange(match[1], match[2])) return { 범위: "범위", min: match[1], max: match[2] };
  match = t.match(AFTER_CAS_LESS_REGEX);
  if (match && inRange(match[1])) return { 범위: "범위", min: "0", max: match[1] };
  match = t.match(AFTER_CAS_GREATER_REGEX);
  if (match && inRange(match[1])) return { 범위: "범위", min: match[1], max: "100" };
  return null;
}

// CAS 번호가 줄 맨 앞에 있는(이름이 앞줄에 따로 나온) 행인지 - 이 경우 바로 앞줄은 이 행의 이름이다
function startsWithCas(line) {
  const casMatch = line && findCas(line);
  return !!casMatch && !line.slice(0, casMatch.index).trim();
}

// PDF 페이지마다 반복되는 머리글/꼬리글("페이지: 3(16)", "MSDS번호:...", 문서 제목 등)이 구성성분 표가
// 페이지를 넘어가는 지점에 끼어들어 물질명 뒤에 붙지 않도록, 문서 전체에서 여러 번 반복되는 줄을 찾는다
function findRepeatedLines(lines, minCount = 3) {
  const counts = new Map();
  lines.forEach((l) => counts.set(l, (counts.get(l) || 0) + 1));
  return new Set([...counts].filter(([, c]) => c >= minCount).map(([l]) => l));
}

// 식별번호 칸의 EC 번호("EC 번호 207-439-9")는 CAS 와 형태가 달라(가운데 3자리) CAS 로는 걸러지지 않고,
// 화학물질명 다음 줄로 따로 떨어져 나와 이름 뒤에 이어붙여지므로 제거한다
function stripEcNumber(text) {
  return text.replace(/EC\s*번호/g, "").replace(/\b\d{3}-\d{3}-\d\b/g, "").replace(/\s+/g, " ").trim();
}

// 화학물질명이 아니라 표 헤더("화학물질명 관용명 및 이명(異名) CAS 번호...")나 각주("* 대체자료
// 기재 승인...")여서, 이름을 앞/뒤 줄로 이어붙일 때 함께 삼키면 안 되는 줄인지 판별한다.
function looksLikeIngredientTableNoise(line) {
  if (/^화학물질명/.test(line)) return true;
  if (/^[*※]/.test(line.trim())) return true;
  return false;
}

// 단일물질 MSDS 에 흔한 세로형 서식: 표의 열이 아니라 "라벨 | 값" 행이 물질마다 위아래로 나열된다.
//   물질명 물(WATER) / 이명(관용명) 디수소 산화물(...) / CAS번호 함 7732-18-5 / 유량 / 100%
// ("함유량" 라벨이 칸 안에서 "함"/"유량" 으로 줄바꿈되어 CAS 줄과 그 다음 줄에 흩어진다)
// 표 형식으로 읽으면 CAS 줄의 라벨("CAS번호 함")과 다음 줄("유량")을 이름으로 오인하므로 따로 처리한다.
// 표 형식의 헤더 줄("물질명 이명(관용명) CAS번호 ...")과 구분하기 위해, "CAS" 라벨로 시작하면서
// 실제 CAS 번호가 들어 있는 줄이 있을 때만 이 서식으로 본다.
const VERTICAL_NAME_REGEX = /^(?:화학\s*)?물질\s*명\s*[:：]?\s*(.+)$/;
const VERTICAL_LABEL_PREFIX_REGEX = /^(?:CAS\s*(?:번호|No\.?)?|(?:함\s*)?유\s*량\s*(?:\(\s*%\s*\))?|함\s*량\s*(?:\(\s*%\s*\))?|함)\s*[:：]?\s*/i;

function isVerticalIngredientFormat(scope) {
  return scope.some((l) => /^CAS/i.test(l.trim()) && hasCas(l));
}

function parseIngredientsVertical(scope) {
  const result = [];
  let current = null;
  let percentFound = false;
  const flush = () => {
    if (current && current.CAS) result.push(buildIngredientRow(current.name, current.CAS, current.함량));
    current = null;
  };

  for (const line of scope) {
    const nameMatch = line.match(VERTICAL_NAME_REGEX);
    if (nameMatch) {
      flush();
      current = { name: nameMatch[1].trim(), CAS: "", 함량: { 범위: "없음", min: "", max: "" } };
      percentFound = false;
      continue;
    }
    if (!current) continue;

    const casMatch = !current.CAS && /^CAS/i.test(line.trim()) && findCas(line);
    const valueText = casMatch ? line.slice(casMatch.index + casMatch.CAS.length) : line.replace(VERTICAL_LABEL_PREFIX_REGEX, "");
    if (casMatch) current.CAS = casMatch.CAS;

    // 함유량은 CAS 를 찾은 뒤에 나오는 값 중 처음 인식되는 것을 쓴다
    if (current.CAS && !percentFound) {
      const fallback = parsePercent(valueText);
      const 함량 = parsePercentAfterCas(valueText) || (fallback.matchedText ? fallback.함량 : null);
      if (함량) {
        current.함량 = 함량;
        percentFound = true;
      }
    }
  }
  flush();
  return result;
}

// CAS 번호 뒤의 텍스트에서 함유량을 찾는다 (CAS 뒤 칸 전용 표기 -> 일반 % 표기 -> "자료없음 숫자" 순)
function parsePercentOnCasLine(restOfLine) {
  const afterCas = parsePercentAfterCas(restOfLine);
  if (afterCas) return { 함량: afterCas, matched: true };
  const general = parsePercent(restOfLine);
  if (general.matchedText) return { 함량: general.함량, matched: true };
  const single = parseSingleColumnPercent(restOfLine);
  if (single) return { 함량: single, matched: true };
  return { 함량: general.함량, matched: false };
}

// 한 줄의 텍스트 조각들을 표 셀 단위로 묶는다. 같은 셀 안의 글자/단어 간격은 몇 pt 이내지만
// 열과 열 사이는 그보다 훨씬 넓게 벌어지므로, 간격이 CELL_GAP 을 넘으면 다른 셀로 본다.
const CELL_GAP = 6;

function splitCells(row) {
  const cells = [];
  let current = null;
  let prevEndX = null;
  for (const it of row.items) {
    const gap = prevEndX === null ? Infinity : it.x - prevEndX;
    if (current && gap <= CELL_GAP) {
      current.text += (gap > 1 ? " " : "") + it.str;
    } else {
      current = { x: it.x, text: it.str };
      cells.push(current);
    }
    prevEndX = it.x + it.width;
  }
  return cells.map((c) => ({ x: c.x, text: c.text.replace(/\s+/g, " ").trim() })).filter((c) => c.text);
}

function median(values) {
  if (!values.length) return 0;
  const sorted = values.slice().sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

// 화학물질명/관용명 셀이 여러 줄로 줄바꿈되고 행 높이 가운데로 세로 정렬된 표(예: 이름은 3줄, 관용명은
// 7줄인데 CAS 는 그 가운데 줄에 1줄)는 줄 순서만으로는 어느 줄이 어느 물질 행에 속하는지 알 수 없다.
// 그래서 텍스트 조각의 x 좌표로 "화학물질명" 열만 골라내고, 그 열의 각 줄을 같은 페이지에서 y 가 가장
// 가까운 CAS 행에 붙인다(셀이 세로 가운데 정렬이라 자기 행의 CAS 줄이 가장 가깝다).
// 열 시작 x 는 CAS 열 왼쪽 셀들의 시작 x 를 모아 2번 이상 반복되는 위치로 정한다 - 첫 번째가 화학물질명,
// 두 번째가 관용명 열이다. 열을 2개 이상 찾지 못하면(행이 1개뿐이거나 셀이 가운데 정렬 등) null 을
// 반환해 기존 줄 기반 로직이 처리하게 한다.
function parseIngredientsByColumns(scope, scopeRows) {
  if (!scopeRows.length || scopeRows.some((r) => !r || !r.items)) return null;

  const headerPos = scope.findIndex((l) => /^(?:화학\s*)?물질\s*명/.test(l));
  const infos = scopeRows.map((r, i) => ({ i, page: r.page, y: r.y, cells: splitCells(r), cas: findCas(scope[i]) }));
  const seenCas = new Set();
  const casRows = infos.filter((info) => {
    if (!info.cas || info.i <= headerPos || seenCas.has(info.cas.CAS)) return false;
    seenCas.add(info.cas.CAS);
    return true;
  });
  if (casRows.length < 2) return null;

  for (const casRow of casRows) {
    const casCell = casRow.cells.find((c) => c.text.includes(casRow.cas.CAS));
    if (!casCell) return null;
    casRow.casX = casCell.x;
  }
  const casColX = Math.min(...casRows.map((r) => r.casX));

  const starts = [];
  infos.forEach((info) => {
    if (info.i <= headerPos) return;
    info.cells.forEach((c) => {
      if (c.x < casColX - 2) starts.push(c.x);
    });
  });
  const clusters = [];
  starts
    .sort((a, b) => a - b)
    .forEach((x) => {
      const last = clusters[clusters.length - 1];
      if (last && x - last.x <= 3) last.count++;
      else clusters.push({ x, count: 1 });
    });
  const columnStarts = clusters.filter((c) => c.count >= 2);
  if (columnStarts.length < 2) return null;
  const aliasColX = columnStarts[1].x;

  const nameText = (info) => {
    const joined = info.cells
      .filter((c) => c.x < aliasColX - 2 && !/^(?:자료\s*없음|-)$/.test(c.text))
      .map((c) => c.text)
      .join(" ");
    return stripEcNumber(joined).replace(/^\d+[.)]\s*/, "");
  };

  const gaps = [];
  infos.slice(1).forEach((info, k) => {
    if (info.page === infos[k].page) gaps.push(Math.abs(infos[k].y - info.y));
  });
  const lineGap = median(gaps.filter((d) => d > 0));
  const firstCasI = casRows[0].i;
  const lastCasI = casRows[casRows.length - 1].i;
  const nameParts = new Map(casRows.map((r) => [r, []]));

  for (const info of infos) {
    if (info.i <= headerPos || looksLikeIngredientTableNoise(scope[info.i])) continue;
    const text = nameText(info);
    if (!text) continue;
    const samePage = casRows.filter((r) => r.page === info.page);
    if (!samePage.length) continue;
    let nearest = samePage[0];
    samePage.forEach((r) => {
      if (Math.abs(r.y - info.y) < Math.abs(nearest.y - info.y)) nearest = r;
    });
    // 첫 CAS 행 위, 마지막 CAS 행 아래의 줄은 표 밖 문장이거나 CAS 없는 병합 행("AWS Classification |
    // A5.22 E308LT0-3")일 수 있다. 가까우면서 CAS 열 오른쪽(함유량 등)에 내용이 없는 줄만 붙인다.
    const outsideRows = info.i < firstCasI || info.i > lastCasI;
    if (outsideRows) {
      if (Math.abs(nearest.y - info.y) > lineGap * 2.5) continue;
      if (info.cells.some((c) => c.x >= casColX - 2)) continue;
    }
    nameParts.get(nearest).push(text);
  }

  const result = [];
  for (const casRow of casRows) {
    // "...2,2'-[(1-" + "methylethylidene)..." 처럼 하이픈에서 줄바꿈된 화학명은 공백 없이 잇는다
    let name = "";
    nameParts.get(casRow).forEach((part) => {
      if (!name) name = part;
      else name = name.endsWith("-") ? name + part : `${name} ${part}`;
    });
    if (!name) return null;
    const { 함량 } = parsePercentOnCasLine(scope[casRow.i].slice(casRow.cas.index + casRow.cas.CAS.length));
    result.push(buildIngredientRow(name, casRow.cas.CAS, 함량));
  }
  return result;
}

// "3. 구성성분의 명칭 및 함유량" 절에서 CAS 번호를 기준으로 구성물질/함유량 행을 best-effort 추출.
// 화학물질명이 길어 표 셀에서 줄바꿈되거나(2줄 이상), 함유량이 CAS 번호와 다른 줄에 있는 서식도
// 최대한 대응한다: CAS 번호가 있는 줄에서 이름 부분이 비어 있으면 바로 위 1~2줄을 이름으로 간주하고,
// 함유량이 없으면 바로 다음 줄에서 찾는다.
function parseIngredients(lines, lineRows) {
  const startIdx = lines.findIndex((l) => /^3\s*[.):]/.test(l) && l.includes("구성성분"));
  const endIdx = lines.findIndex((l, i) => i > startIdx && startIdx >= 0 && /^4\s*[.):]/.test(l));
  const repeatedLines = findRepeatedLines(lines);
  const scopeStart = startIdx >= 0 ? startIdx : 0;
  const scopeEnd = startIdx >= 0 ? (endIdx > startIdx ? endIdx : Math.min(lines.length, startIdx + 80)) : lines.length;
  const scopeIndexes = [];
  for (let i = scopeStart; i < scopeEnd; i++) {
    if (!repeatedLines.has(lines[i]) || hasCas(lines[i])) scopeIndexes.push(i);
  }
  const scope = scopeIndexes.map((i) => lines[i]);

  if (isVerticalIngredientFormat(scope)) {
    const vertical = parseIngredientsVertical(scope);
    if (vertical.length > 0) return vertical;
  }

  // 좌표로 열을 나눌 수 있는 표는 그 방식을 우선 쓰고, 열 구분이 안 되는 문서만 아래 줄 순서 기반 로직으로 처리한다
  if (lineRows) {
    const byColumns = parseIngredientsByColumns(scope, scopeIndexes.map((i) => lineRows[i]));
    if (byColumns) return byColumns;
  }

  const result = [];
  const consumed = new Set();
  // 절을 못 찾아 문서 전체를 훑는 경우, 같은 CAS 가 11.독성/15.법적규제 절 등에서 다시 나오므로 처음 것만 쓴다
  const seenCas = new Set();

  for (let idx = 0; idx < scope.length; idx++) {
    if (consumed.has(idx)) continue;
    const line = scope[idx];
    const casMatch = findCas(line);
    if (!casMatch) continue;

    const { CAS } = casMatch;
    if (seenCas.has(CAS)) continue;

    // 관용명/이명 칸이 "자료없음" 이나 "-" 로 비어 있으면 이름 뒤에 붙어 나오므로 떼어낸다
    let namePart = line
      .slice(0, casMatch.index)
      .replace(/^\d+[.)]?\s*/, "")
      .replace(/(?:\s*(?:자료\s*없음|-))+\s*$/, "")
      .trim();

    // 이 줄에 이름이 없으면(화학물질명 칸이 넓어 앞줄들에 이미 다 나온 서식) 바로 위 1~2줄을 이름으로 본다
    if (!namePart) {
      const nameLines = [];
      for (let back = 1; back <= 2; back++) {
        const prevIdx = idx - back;
        if (prevIdx < 0) break;
        const prevLine = scope[prevIdx];
        if (!prevLine || consumed.has(prevIdx)) break;
        if (hasCas(prevLine) || PERCENT_REGEX.test(prevLine) || looksLikeIngredientTableNoise(prevLine)) break;
        nameLines.unshift(prevLine);
        consumed.add(prevIdx);
      }
      namePart = nameLines.join(" ").trim();
    }
    if (!namePart) continue;

    // 반대로 이름이 CAS/함유량이 있는 줄에서 시작해 다음 줄로 넘어가며 끝나는 경우
    // (예: "Distillates (petroleum), - 64742-54-7 60.0~70.0" 다음 줄에 "hydrotreated heavy paraffinic")
    // - CAS/함유량/다음 절 제목이 없는 순수 이어지는 텍스트 한 줄까지만 이름 뒤에 붙인다
    // 단, 그 다음 줄이 CAS 로 시작하는 행이면 다음 줄은 이어지는 이름이 아니라 다음 물질의 이름이다
    // (예: "이산화티타늄 ... 2~5" / "산화지르코늄 지르코늄이산화물" / "1314-23-4 <2")
    const nextIdx = idx + 1;
    const nextLine = scope[nextIdx];
    if (
      nextLine &&
      !startsWithCas(scope[nextIdx + 1]) &&
      !consumed.has(nextIdx) &&
      !hasCas(nextLine) &&
      !PERCENT_REGEX.test(nextLine) &&
      !looksLikeIngredientTableNoise(nextLine) &&
      !/^\d+\s*[.):]\s*[가-힣]/.test(nextLine)
    ) {
      namePart = `${namePart} ${stripEcNumber(nextLine)}`.trim();
      consumed.add(nextIdx);
    }

    const percentOnSameLine = parsePercentOnCasLine(line.slice(casMatch.index + CAS.length));
    let 함량 = percentOnSameLine.함량;
    if (!percentOnSameLine.matched && scope[idx + 1] && !consumed.has(idx + 1) && !hasCas(scope[idx + 1])) {
      // 함유량이 CAS 번호와 같은 줄에 없으면 바로 다음 줄에서 찾는다 (단, 다음 물질의 CAS 줄이면 제외)
      const nextResult = parsePercent(scope[idx + 1]);
      if (nextResult.matchedText) {
        함량 = nextResult.함량;
        consumed.add(idx + 1);
      }
    }

    seenCas.add(CAS);
    result.push(buildIngredientRow(namePart, CAS, 함량));
  }
  return result;
}

// 개별 항목 파싱은 서로 독립적이어야 한다 - 한 항목의 서식이 예상과 달라 파싱 중 예외가 나더라도
// 나머지 항목까지 전부 빈 값이 되지 않도록 각 필드를 독립적으로 방어한다
function safe(fn, fallback) {
  try {
    return fn();
  } catch (e) {
    return fallback;
  }
}

// 텍스트 기반 판정과 이미지 기반 판정을 합집합으로 합친다. 실제 제조사 MSDS는 그림문자를
// 이미지로만 넣고 텍스트 캡션이 없는 경우가 대부분이라 이미지 판정이 주로 보강 역할을 하지만,
// 반대로 이미지 추출이 실패하는 케이스(벡터 픽토그램 등)도 있어 어느 한쪽만 신뢰하지 않는다.
// 교집합으로 하면 한쪽이 놓친 그림문자가 통째로 누락되므로, 안전보건 문서 특성상 누락보다
// 오탐(사람이 검토 화면에서 체크 해제)이 더 안전하다고 보고 합집합을 채택했다.
function mergePictogramResults(textResult, imageResult) {
  const merged = { ...textResult };
  for (const [id, checked] of Object.entries(imageResult)) {
    if (checked) merged[id] = true;
  }
  return merged;
}

// PDF 에서 경고표지 요소(그림문자, 신호어)만 다시 읽는다.
// 그림문자 이미지 매칭(pictogramImageMatcher)이 추가되기 전에 OCR 로 등록된 문서는 그림문자가 모두 비어 있어,
// 첨부된 MSDS PDF 를 다시 읽어 그림문자만 채우는 일괄 재추출(PopupGHSPictogramRescan.vue)에서 사용한다.
// 반환값: { 그림문자: {GHS01: bool, ...}, 신호어: "없음"|"경고"|"위험", matches: 이미지 매칭 상세 }
// options.maxPages: 이미지 매칭을 앞쪽 N페이지로 제한한다(그림문자는 2절 "유해성·위험성"에 있어 문서 앞부분에만 나온다).
// 그림문자 항목에 그림 대신 "해당없음" / "자료없음" / "없음" 이 적힌 문서인가.
// 라벨과 같은 줄에 있거나, 라벨만 있는 줄 바로 다음 줄에 그 문구만 있는 경우를 본다.
// (다음 줄이 "신호어 ..." 처럼 다른 항목이면 그림문자 값이 아니다)
function hasPictogramDeclaredNone(lines) {
  const NONE = /(해당\s*없음|자료\s*없음|그림\s*표기\s*없음|^없\s*음$|^none$|not\s+applicable)/i;
  const LABEL = /그\s*림\s*문\s*자|pictograms?/i;
  for (let i = 0; i < lines.length; i++) {
    if (!LABEL.test(lines[i])) continue;
    // 라벨 뒤의 괄호 주석("그림문자 (GHS KR)")은 값이 아니므로 떼어낸다(실측: 다음 줄에 "해당없음"이 오는 서식)
    const rest = stripBullets(lines[i].replace(/.*(그\s*림\s*문\s*자|pictograms?)/i, "").replace(/\([^)]*\)/g, ""));
    if (rest && NONE.test(rest)) return true;
    if (!rest && i + 1 < lines.length) {
      const next = stripBullets(lines[i + 1]);
      if (!/신\s*호\s*어|유해|위험\s*문구|예방|signal|hazard/i.test(next) && NONE.test(next)) return true;
      // 그림문자 칸이 비어 있고 바로 다음 항목인 신호어가 "해당없음/자료없음/없음/분류되지 않음"이면 그림문자가 없는 문서다
      // (그림문자가 있으면 신호어도 반드시 있다. 실측: 소포제 "신호어 해당없음", 전극봉 "신호어 자료없음", Shell "신호어 : 신호어 없음")
      if (/^신\s*호\s*어(?![\s\S]*(위\s*험|경\s*고))[\s\S]*(해당\s*없음|자료\s*없음|없\s*음|분류되지\s*않음)$/.test(next)) return true;
    }
  }
  return false;
}

// 문서 전체가 GHS 분류 대상이 아니라고 적힌 경우(경고표지 항목 자체가 없는 서식이 많다).
// 개별 유해성 항목의 "분류되지 않음" 과 헷갈리지 않도록 "유해성·위험성 분류 : 분류되지 않음" 같은 총괄 문구만 본다.
const NOT_CLASSIFIED_PATTERNS = [
  /유해성\s*[·.]?\s*위험성\s*(?:의\s*)?분류\s*[:：]?\s*분류되지\s*않음/,
  /\(GHS\)\s*분류\s*[:：]?\s*분류되지\s*않음/,
  /분류할\s*수\s*없음/,
  /유해성\s*물질\s*또는\s*혼합물이\s*아님/,
  /GHS\s*라벨링\s*비요구/,
  /표지\s*부착\s*[:：]?\s*해당\s*없음/,
  /^not\s+classified/i,
];

function hasNotClassifiedStatement(lines) {
  for (let i = 0; i < Math.min(lines.length, 120); i++) {
    const line = lines[i];
    if (NOT_CLASSIFIED_PATTERNS.some((re) => re.test(line))) return true;
    // 영문 서식: "2. Hazards Identification" 바로 다음 줄이 "Not applicable"
    if (/hazards?\s+identification/i.test(line) && i + 1 < lines.length && /^not\s+applicable/i.test(stripBullets(lines[i + 1]))) return true;
  }
  return false;
}

// 미인식 원인을 나누기 위한 텍스트 정보: 라벨은 있는데 그림을 못 찾은 것(매칭 한계)과
// 라벨조차 없는 것(스캔 이미지 PDF, 경고표지 항목 없음, 다른 서식)은 보완 방향이 다르다.
// 글꼴에 유니코드 매핑이 없어 글자가 빠지고 구두점만 남는 문서인가(실측: Voith 계열 MSDS 2절 — ". ·", ": :", "( GHS )." 만 남음).
// 이런 문서는 "그림문자 해당없음" 이 눈에는 보여도 텍스트로는 읽을 수 없으므로 스캔본과 같이 다뤄야 한다.
function hasBrokenText(lines) {
  const sample = lines.slice(0, 120);
  const punctuationOnly = sample.filter((line) => /^[\s.,:;()\-·ᆞ–—/%|]+$/.test(line)).length;
  return punctuationOnly >= 4 && punctuationOnly / Math.max(1, sample.length) >= 0.15;
}

function buildTextInfo(lines, signalWord) {
  return {
    hasPictogramLabel: lines.some((line) => /그\s*림\s*문\s*자|pictograms?/i.test(line)),
    brokenText: hasBrokenText(lines),
    declaredNone: hasPictogramDeclaredNone(lines),
    notClassified: hasNotClassifiedStatement(lines),
    signalNone: signalWord === "없음", // 신호어 "없음/해당없음/자료없음/분류되지 않음" 을 실제로 읽은 경우(못 읽은 "" 과 다름)
    signalAmbiguous: hasAmbiguousSignalWord(lines),
    length: lines.join("").replace(/\s+/g, "").length,
  };
}

async function extractPictogramsFromLoadedPdf(pdf, options) {
  const lineRows = stripPrintNoise(await extractLines(pdf));
  const lines = lineRows.map((row) => row.text);

  const textPictograms = safe(() => parsePictograms(lines), {});
  const signalWord = safe(() => parseSignalWord(lines), "");
  const text = buildTextInfo(lines, signalWord);
  // 그림문자 라벨은 있는데 없음 표기도 없는 문서에서만, 이미지에서 못 찾았을 때 페이지를 렌더링해 다시 찾는다(벡터 그림문자)
  const renderFallback = (text.hasPictogramLabel || text.brokenText) && !text.declaredNone && !text.notClassified && !text.signalNone;
  let 그림문자 = textPictograms;
  let matches = [];
  let unmatchedPictograms = 0;
  try {
    const imageResult = await matchPictogramsFromPdf(pdf, { maxPages: options.maxPages, renderFallback });
    그림문자 = mergePictogramResults(textPictograms, imageResult.pictograms);
    matches = imageResult.matches;
    unmatchedPictograms = imageResult.unmatched || 0;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.debug("[msdsPdfParser] 이미지 기반 그림문자 매칭 실패, 텍스트 판정만 사용:", e);
  }

  return {
    그림문자,
    신호어: signalWord || "없음",
    matches,
    unmatchedPictograms, // 그림문자처럼 생겼지만 못 알아본 이미지 수(일부만 인식 판정용)
    text,
  };
}


// PDF File 을 파싱해 Write.vue 의 form 에 바로 병합할 수 있는 형태로 반환한다
async function parseLoadedMsdsPdf(pdf) {
  const lineRows = stripPrintNoise(await extractLines(pdf));
  const lines = lineRows.map((row) => row.text);

  // 자동인식이 잘 안 될 때 원인을 확인할 수 있도록 추출된 줄을 콘솔에 남겨둔다
  // eslint-disable-next-line no-console
  console.debug("[msdsPdfParser] extracted lines:", lines);

  const 제품명 = safe(() => {
    const byLabel = findValueAfterLabel(lines, /제품\s*명\s*(?:\([^)]*\))?/) || findValueAfterLabel(lines, /제품의?\s*명칭/);
    if (byLabel) return byLabel;

    // 이 앱 자체 인쇄화면(Print.vue)은 "제품명" 이라는 라벨 없이 제품명을 큰 제목(h1)으로만 표기하며,
    // 그 값이 언제나 문서의 첫 줄이다 - 값 자체가 우연히 "제품명" 으로 시작하더라도(예: 실제 값이
    // "제품명(물질명)" 이라는 문자열인 경우) 이 서식에서는 무조건 값이지 라벨이 아니므로, 절 제목
    // 패턴(UNIVERSAL_SECTION_BOUNDARIES)으로 걸러내지 않는다.
    const looksLikeOwnPrintFormat = lines.some((l) => /^(공급자\s*정보|유해\s*[·ㆍ]?\s*위험\s*문구|예방\s*조치\s*문구)/.test(l.trim()));
    const firstLine = lines[0] && lines[0].trim();
    if (looksLikeOwnPrintFormat && firstLine && firstLine !== "-") {
      return firstLine;
    }
    return "";
  }, "");
  const 제조사 = safe(() => {
    return findValueByLabelPatterns(lines, [
      /제조사\s*\(?\s*공급사?\s*\)?/,
      /회사명/,
      /제조자\s*\/\s*공급자/,
      /제조자(?!\s*정보)/,
      /공급자(?!\s*정보)/,
    ]);
  }, "");
  const 용도 = safe(() => {
    // 절 제목("나. 제품의 권고 용도와 사용상의 제한")에도 "권고 용도"가 들어 있어 라벨로 오인하면
    // 제목의 나머지("와 사용상의 제한")를 값으로 가져가므로, 제목 줄은 빼고 줄 첫머리의 "권고 용도" 라벨부터 찾는다
    const valueLines = lines.filter((l) => !/^[가-힣]\s*[.)]\s*제품의\s*(?:권고\s*)?용도/.test(l.trim()));
    return findValueAfterLabel(valueLines, /^권고\s*용도/) || findValueAfterLabel(valueLines, /(?:제품의\s*용도|용도)/);
  }, "");

  const msds등록번호 = safe(() => {
    const match = findFirstMatch(lines, /(?:고유번호|MSDS\s*(?:등록)?번호)\s*[:：]?\s*([A-Za-z0-9-]+)/i);
    return match ? match[1] : "";
  }, "");

  const 제조사전화번호 = safe(() => {
    const phoneLineIdx = lines.findIndex((l) => /(?:전화번호|긴급전화|Tel|TEL)/.test(l));
    if (phoneLineIdx < 0) return "";
    for (let i = phoneLineIdx; i < Math.min(phoneLineIdx + 3, lines.length); i++) {
      const phone = extractPhone(lines[i]);
      if (phone) return phone;
    }
    return "";
  }, "");

  const hazardSectionLines = safe(() => getHazardSectionLines(lines), lines);
  const 유해위험문구 = safe(() => {
    const withCode = parseHazardStatements(hazardSectionLines);
    return withCode.length > 0 ? withCode : parseHazardStatementsNoCode(lines);
  }, []);
  const 예방조치문구 = safe(() => {
    const withCode = parsePrecautionStatements(hazardSectionLines);
    return withCode.length > 0 ? withCode : parsePrecautionStatementsNoCode(lines);
  }, []);

  const textPictograms = safe(() => parsePictograms(lines), {});
  // 이미지 매칭은 페이지 전체를 순회하며 픽셀 단위로 비교하기 때문에, 텍스트 파싱보다 실패
  // 가능성이 높은 별도 경로로 본다 - 여기서 예외가 나도 텍스트 판정 결과는 그대로 살린다.
  let 그림문자 = textPictograms;
  try {
    const { pictograms: imagePictograms, matches } = await matchPictogramsFromPdf(pdf);
    그림문자 = mergePictogramResults(textPictograms, imagePictograms);
    // eslint-disable-next-line no-console
    console.debug("[msdsPdfParser] 이미지 기반 그림문자 매칭:", matches);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.debug("[msdsPdfParser] 이미지 기반 그림문자 매칭 실패, 텍스트 판정만 사용:", e);
  }

  return {
    제품명,
    msds등록번호,
    제조사,
    제조사전화번호,
    용도,
    경고표지요소: {
      그림문자,
      신호어: safe(() => parseSignalWord(lines), "") || "없음",
      유해위험문구,
      예방조치문구,
    },
    구성성분: safe(() => parseIngredients(lines, lineRows), []),
    취급부서: [],
  };
}

// ---- 공개 API. 내부 파싱 함수들이 모두 정의된 뒤에 두어 no-use-before-define 을 피한다. ----

export async function extractPictogramsFromPdf(file, options = {}) {
  const pdf = await loadPdfDocument(file);
  try {
    return await extractPictogramsFromLoadedPdf(pdf, options);
  } finally {
    await destroyPdfDocument(pdf);
  }
}

export async function parseMsdsPdf(file) {
  const pdf = await loadPdfDocument(file);
  try {
    return await parseLoadedMsdsPdf(pdf);
  } finally {
    await destroyPdfDocument(pdf);
  }
}
